import React, { useEffect, useRef, useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { FILE_ACCEPT, MESSAGE_TEXT_LIMIT } from '../constants';
import { useSupportStore } from '../state/use-support-store';
import { createIdempotencyKey, formatFileSize, kindOfFile, validateAttachment } from '../utils';

const Composer = ({ threadId }) => {
  const { sendMessage, sending, uploadProgress } = useSupportStore();

  const inputRef = useRef(null);
  const idempotencyKeyRef = useRef('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [attachmentId, setAttachmentId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setText('');
    setFile(null);
    setAttachmentId(null);
    setError('');
    idempotencyKeyRef.current = '';
  }, [threadId]);

  useEffect(() => {
    if (!file || kindOfFile(file) !== 'image') {
      setPreviewUrl('');
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clearFile = () => {
    setFile(null);
    setAttachmentId(null);
    setError('');
    idempotencyKeyRef.current = '';
  };

  const onPick = (event) => {
    const next = event.target.files?.[0];
    event.target.value = '';
    if (!next) return;

    const validationError = validateAttachment(next);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(next);
    setAttachmentId(null);
    setError('');
    idempotencyKeyRef.current = '';
  };

  const onSend = async () => {
    const trimmed = text.trim();
    if (sending || (!trimmed && !file && !attachmentId)) return;
    if (trimmed.length > MESSAGE_TEXT_LIMIT) {
      setError(`Сообщение длиннее ${MESSAGE_TEXT_LIMIT} символов`);
      return;
    }

    setError('');
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createIdempotencyKey();

    const result = await sendMessage({
      text: trimmed,
      file: attachmentId ? null : file,
      attachmentId,
      idempotencyKey: idempotencyKeyRef.current,
    });

    if (result?.ok) {
      idempotencyKeyRef.current = '';
      setText('');
      clearFile();
      return;
    }

    if (result?.attachmentId) setAttachmentId(result.attachmentId);
  };

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      {file ? (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-2">
          {previewUrl ? <img src={previewUrl} alt="" className="h-12 w-12 rounded object-cover" /> : null}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-gray-800">{file.name}</p>
            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
            {sending && uploadProgress !== null ? (
              <div className="mt-1 h-1 overflow-hidden rounded bg-gray-200">
                <div className="h-1 bg-orange-500" style={{ width: `${uploadProgress}%` }} />
              </div>
            ) : null}
          </div>
          <button type="button" aria-label="Убрать файл" disabled={sending} onClick={clearFile} className="text-gray-400 hover:text-gray-700">
            <CloseIcon fontSize="small" />
          </button>
        </div>
      ) : null}

      {error ? <p className="mb-2 text-sm text-red-500">{error}</p> : null}

      <div className="flex items-end gap-2">
        <button
          type="button"
          aria-label="Прикрепить файл"
          disabled={sending}
          onClick={() => inputRef.current?.click()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:text-gray-300">
          <AttachFileIcon />
        </button>
        <input ref={inputRef} type="file" accept={FILE_ACCEPT} hidden onChange={onPick} />

        <textarea
          value={text}
          rows={2}
          maxLength={MESSAGE_TEXT_LIMIT}
          disabled={sending}
          placeholder="Сообщение"
          onChange={(event) => {
            setText(event.target.value);
            idempotencyKeyRef.current = '';
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              onSend();
            }
          }}
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-2xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-400"
        />

        <button
          type="button"
          aria-label="Отправить"
          disabled={sending || (!text.trim() && !file && !attachmentId)}
          onClick={onSend}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white disabled:bg-gray-300">
          <SendIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default Composer;
